/**
 * 運送PL(Board・2026-10-23)。形は SC Board_v2「運送」シート。集計は unsoPl.ts、読み込みは unsoPlRepo.ts。
 *
 *  - 列: 項目 / 単価 / 稼働日数・数量 / 選んだ月の金額(速報・確定)/ 前の3か月 / 算出方法 / ガソリン区分 / 引用元
 *  - 行は開け閉めできる(元請け > 拠点 > コース > ドライバー。最初は区分と人件費・ガソリン代まで開く)
 *  - スマホ(900px以下)は選んだ月の金額だけの縦並び(横スクロールなし・2026-09-07 指示)
 *  - 見られる人: 全社PL を見られる人・経理/承認者(確定の権限)・運送の部門長(appTarget.ts の unsoPl)
 *
 * 書き込みはしない(表示のみ)
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { formatYen } from '@core/lib/money';
import { toMonthLabel } from '@core/lib/date';
import { useIsMobile } from '@core/lib/useIsMobile';
import type { MonthStr, RoutinePlLine, Transaction } from '@core/lib/types';
import { buildUnsoPl, columnMonths, hasAmount, openableKeys, type StageMark, type UnsoNode, type UnsoPl } from './unsoPl';
import { fetchRoutinePlLines, fetchUnsoTransactions } from './unsoPlRepo';

type Loaded = { lines: RoutinePlLine[]; transactions: Transaction[]; routineError: boolean };

const STAGE_LABEL: Record<Exclude<StageMark, null>, { text: string; cls: string }> = {
  live: { text: '速報', cls: 'b-plan' },
  confirmed: { text: '確定', cls: 'b-ok' },
  mixed: { text: '一部確定', cls: '' },
};

const shortMonth = (m: MonthStr) => `${Number(m.slice(5, 7))}月`;

function StageBadge({ stage }: { stage: StageMark }) {
  if (!stage) return null;
  const s = STAGE_LABEL[stage];
  return <span className={`badge ${s.cls}`} style={{ marginLeft: 6, padding: '1px 6px', fontSize: 10.5 }}>{s.text}</span>;
}

function qtyText(n: UnsoNode): string {
  if (n.quantity === null) return '';
  return `${n.quantity.toLocaleString('ja-JP')}${n.quantityUnit ?? ''}`;
}

/** 最初に開いておく行: 区分の見出しと、人件費・ガソリン代 */
function initialOpen(pl: UnsoPl): Set<string> {
  const keys = new Set<string>();
  for (const r of pl.rows) {
    if (r.children.length > 0) keys.add(r.key);
    for (const c of r.children) if (c.key === 'cogs/labor' || c.key === 'cogs/fuel') keys.add(c.key);
  }
  return keys;
}

export default function UnsoPlPage() {
  const { accounts, subAccounts, loading: mastersLoading } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const mobile = useIsMobile();
  const [hideZero, setHideZero] = useState(true);
  const [open, setOpen] = useState<Set<string> | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const months = useMemo(() => columnMonths(month, 3), [month]);
  const requestNo = useRef(0);

  const load = useCallback(async () => {
    if (mastersLoading) return;
    const no = ++requestNo.current;
    setLoading(true);
    try {
      const [routine, transactions] = await Promise.all([
        fetchRoutinePlLines(months).then(
          (lines) => ({ lines, error: false }),
          () => ({ lines: [] as RoutinePlLine[], error: true }),
        ),
        fetchUnsoTransactions(months),
      ]);
      if (no !== requestNo.current) return;
      setLoaded({ lines: routine.lines, transactions, routineError: routine.error });
    } catch (e) {
      if (no !== requestNo.current) return;
      toast(e instanceof Error ? e.message : '運送PL を読み込めませんでした');
      setLoaded(null);
    } finally {
      if (no === requestNo.current) setLoading(false);
    }
  }, [mastersLoading, months, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const pl = useMemo(
    () => (loaded ? buildUnsoPl({ lines: loaded.lines, transactions: loaded.transactions, accounts, subAccounts, months }) : null),
    [loaded, accounts, subAccounts, months],
  );
  const openSet = useMemo(() => open ?? (pl ? initialOpen(pl) : new Set<string>()), [open, pl]);
  const allKeys = useMemo(() => (pl ? openableKeys(pl.rows) : []), [pl]);

  function toggle(key: string) {
    setOpen(() => {
      const next = new Set(openSet);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const visible = (n: UnsoNode) => !hideZero || n.kind === 'profit' || n.kind === 'section' || hasAmount(n, months);

  return (
    <section className="screen acct-pl">
      <div className="card">
        <h3>運送PL({toMonthLabel(month)})</h3>

        <div className="acct-toolbar">
          <span className="hint">列: {months.map(shortMonth).join('・')}(選んだ月は {shortMonth(month)})</span>
          <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', margin: 0, cursor: 'pointer', fontSize: 12.5 }}>
              <input type="checkbox" style={{ margin: 0, width: 15, height: 15 }} checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
              金額のない行を隠す
            </label>
            <button className="chip" onClick={() => setOpen(openSet.size >= allKeys.length ? new Set() : new Set(allKeys))} disabled={allKeys.length === 0}>
              {openSet.size >= allKeys.length && allKeys.length > 0 ? 'すべて閉じる' : 'すべて開く'}
            </button>
          </span>
        </div>

        {loaded?.routineError && (
          <p className="hint" style={{ margin: '0 0 8px', color: 'var(--red)' }}>
            Routine の数字を読めませんでした(権限ルールが反映されていない可能性があります)。Gate の取引だけを出しています。
          </p>
        )}
        {pl && !loaded?.routineError && pl.routineLineCount === 0 && (
          <p className="hint" style={{ margin: '0 0 8px' }}>
            Routine の数字はまだ届いていません(Routine と未接続)。売上・人件費・ガソリン代は、Routine とつながると元請け・拠点・ドライバーごとに出ます。
          </p>
        )}
        {pl && pl.unknownAccountCount > 0 && (
          <p className="hint" style={{ margin: '0 0 8px', color: 'var(--red)' }}>
            勘定科目マスタに無い科目の取引が {pl.unknownAccountCount} 件あり、この表には入っていません。
          </p>
        )}

        {loading || !pl ? (
          <p className="empty">{loading ? '読み込み中…' : '表示できるデータがありません'}</p>
        ) : mobile ? (
          <MobileList pl={pl} open={openSet} toggle={toggle} visible={visible} />
        ) : (
          <PcTable pl={pl} open={openSet} toggle={toggle} visible={visible} />
        )}

        {pl && pl.gatePending.count > 0 && (
          <p className="hint" style={{ marginTop: 8 }}>
            このほかに、{shortMonth(month)}の申請中・承認待ちの経費が {pl.gatePending.count} 件・{formatYen(pl.gatePending.amount)} あります(見込み。表には入れていません)。
          </p>
        )}
        <p className="hint" style={{ marginTop: 12 }}>
          引用元 Routine = Routine の締めと同じ計算の数字(速報 = 締め前・確定 = 締めの確定)。引用元 Gate = Gate の取引(承認済み以降を計上日の月で集計)。
          利益は v8 と同じ計算(粗利 = 売上 − 原価、営業利益 = 粗利 − 販管、経常利益 = 営業利益 + 営業外収益 − 営業外費用、税引前 = 経常利益 + 特別利益 − 特別損失)。
          この表は全社PL の5行には入っていません。
        </p>
      </div>
    </section>
  );
}

type ViewProps = { pl: UnsoPl; open: Set<string>; toggle: (key: string) => void; visible: (n: UnsoNode) => boolean };

function PcTable({ pl, open, toggle, visible }: ViewProps) {
  const past = pl.months.slice(0, -1);
  const cols = 7 + past.length;
  const rows: ReactElement[] = [];

  const walk = (n: UnsoNode) => {
    if (!visible(n)) return;
    const kids = n.children.filter(visible);
    const canOpen = kids.length > 0;
    const isOpen = canOpen && open.has(n.key);
    const cls = n.kind === 'section' ? 'sec' : n.kind === 'profit' ? 'profit' : canOpen ? 'pl-row' : undefined;
    rows.push(
      <tr key={n.key} className={cls} onClick={canOpen ? () => toggle(n.key) : undefined} style={canOpen ? { cursor: 'pointer' } : undefined}>
        <td>
          <div className="pl-name" style={{ paddingLeft: 8 + n.depth * 16, whiteSpace: 'nowrap' }}>
            <span className={`chev${isOpen ? ' open' : ''}`}>{canOpen ? '▶' : ''}</span>
            {n.label}
          </div>
        </td>
        <td className="num">{n.unitPrice === null ? '' : formatYen(n.unitPrice)}</td>
        <td className="num">{qtyText(n)}</td>
        <td className="num" style={{ fontWeight: n.depth === 0 ? 700 : 400, whiteSpace: 'nowrap' }}>
          {formatYen(n.amounts[pl.month] ?? 0)}
          {n.source === 'Routine' && <StageBadge stage={n.stages[pl.month] ?? null} />}
        </td>
        {past.map((m) => (
          <td key={m} className="num hint">
            {formatYen(n.amounts[m] ?? 0)}
          </td>
        ))}
        <td className="hint" style={{ minWidth: 150 }}>{n.method ?? ''}</td>
        <td className="hint" style={{ whiteSpace: 'nowrap' }}>{n.gasClass ?? ''}</td>
        <td className="hint" style={{ whiteSpace: 'nowrap' }}>{n.source ?? ''}</td>
      </tr>,
    );
    if (isOpen) kids.forEach(walk);
    if (n.kind === 'section' && kids.length === 0) {
      rows.push(
        <tr key={`${n.key}/empty`}>
          <td colSpan={cols} className="hint" style={{ paddingLeft: 26 }}>
            金額のある行はありません
          </td>
        </tr>,
      );
    }
  };
  pl.rows.forEach(walk);

  return (
    <table className="no-stack acct-table">
      <thead>
        <tr>
          <th style={{ width: '26%' }}>項目</th>
          <th className="num">単価</th>
          <th className="num" style={{ whiteSpace: 'nowrap' }}>稼働日数・数量</th>
          <th className="num" style={{ whiteSpace: 'nowrap' }}>{shortMonth(pl.month)}(選んだ月)</th>
          {past.map((m) => (
            <th key={m} className="num">
              {shortMonth(m)}
            </th>
          ))}
          <th style={{ whiteSpace: 'nowrap' }}>算出方法</th>
          <th style={{ whiteSpace: 'nowrap' }}>ガソリン区分</th>
          <th style={{ whiteSpace: 'nowrap' }}>引用元</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  );
}

function MobileList({ pl, open, toggle, visible }: ViewProps) {
  const items: ReactElement[] = [];
  const walk = (n: UnsoNode) => {
    if (!visible(n)) return;
    const kids = n.children.filter(visible);
    const canOpen = kids.length > 0;
    const isOpen = canOpen && open.has(n.key);
    const detail = [
      n.unitPrice !== null ? `単価 ${formatYen(n.unitPrice)}` : '',
      qtyText(n) ? `× ${qtyText(n)}` : '',
      n.gasClass ? `ガソリン ${n.gasClass}` : '',
      n.source ?? '',
    ].filter(Boolean);
    const content = (
      <>
        <div className="acct-main">
          <span>
            {canOpen && <span className={`chev${isOpen ? ' open' : ''}`}>▶</span>}
            {n.label}
          </span>
          <span className="num" style={{ whiteSpace: 'nowrap' }}>
            {formatYen(n.amounts[pl.month] ?? 0)}
            {n.source === 'Routine' && <StageBadge stage={n.stages[pl.month] ?? null} />}
          </span>
        </div>
        {detail.length > 0 && n.kind === 'leaf' && (
          <div className="acct-sub-line">
            {detail.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
        )}
      </>
    );
    if (n.kind === 'section') {
      items.push(
        <button key={n.key} type="button" className="acct-sec" style={{ width: '100%', textAlign: 'left', border: 'none', fontFamily: 'inherit' }} onClick={canOpen ? () => toggle(n.key) : undefined}>
          {content}
        </button>,
      );
    } else if (n.kind === 'profit') {
      items.push(
        <div key={n.key} className="acct-item acct-profit">
          {content}
        </div>,
      );
    } else {
      items.push(
        <button
          key={n.key}
          type="button"
          className="acct-item"
          style={{ paddingLeft: 10 + (n.depth - 1) * 14 }}
          onClick={canOpen ? () => toggle(n.key) : undefined}
          disabled={!canOpen}
        >
          {content}
        </button>,
      );
    }
    if (isOpen) kids.forEach(walk);
  };
  pl.rows.forEach(walk);
  return <div className="acct-list">{items}</div>;
}
